// Go back and grab the last commit before 4/17/2026 to get the last working version of this code.
import React, { useState } from 'react'
import SubscribeButton from './SubscribeButton'
import { getOrCreateIapUserId } from './iapUserId'

export default function Paywall({ userId }) {
	// TEMP: disable paywall completely
	// return null

	const [restoreBusy, setRestoreBusy] = useState(false)
	const [restoreMsg, setRestoreMsg] = useState('')
	// *************************************************************
	//
	// Google Play reviewer access
	//
	// This is ONLY used by Google Play reviewers so they can bypass
	// the subscription paywall during app review.
	//
	// The actual review code is NOT stored in the frontend.
	// It is validated by the backend against GOOGLE_REVIEW_CODE
	// stored in Heroku.
	//
	// *************************************************************

	const [showReviewerAccess, setShowReviewerAccess] = useState(false)
	const [reviewCode, setReviewCode] = useState('')
	const [reviewMsg, setReviewMsg] = useState('')
	const [reviewBusy, setReviewBusy] = useState(false)

	const inAppApple =
		typeof window !== 'undefined' && !!window.executeWhenAppReady

	//************************************************************* */
	//
	//  maybe comment out these three lines of code once you get Android
	//  yearly subscription working.  I have this in here right now to determine
	//  if the product were using is an Android.  If it is, we don't want to show
	//  the yearly subscription button option.  this code helps us determine this
	//
	//
	const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : ''
	const isAndroid = /android/i.test(userAgent)
	const isIOS = /iPhone|iPad|iPod/i.test(userAgent)
	//
	//
	//
	//************************************************************* */
	const showAppleYearly = inAppApple && isIOS

	const effectiveUserId = userId || getOrCreateIapUserId()

	const handleRestore = () => {
		setRestoreMsg('')

		if (!inAppApple) {
			setRestoreMsg('Restore is only available in the iOS app.')
			return
		}

		setRestoreBusy(true)

		window.executeWhenAppReady(async () => {
			try {
				if (typeof window.restorePurchases !== 'function') {
					setRestoreMsg(
						'Restore is not ready yet. Force-close the app and reopen, then try again.',
					)
					return
				}

				setRestoreMsg('Restoring purchases…')

				await window.restorePurchases(effectiveUserId)

				setTimeout(() => {
					window.location.reload()
				}, 600)
			} catch (e) {
				console.error('Restore error:', e)
				setRestoreMsg('Restore error: ' + (e?.message || String(e)))
			} finally {
				setRestoreBusy(false)
			}
		})
	}

	// *************************************************************
	//
	// Google Play reviewer paywall bypass
	//
	// Sends the reviewer-entered code and this device's IAP userId
	// to the backend.
	//
	// If the backend approves the code, it grants a temporary
	// "google-review-access" entitlement and we reload the app.
	//
	// Normal customers never receive access from this method unless
	// they know the private Google review code.
	//
	// *************************************************************

	const handleGoogleReviewerAccess = async () => {
		setReviewMsg('')

		if (!reviewCode.trim()) {
			setReviewMsg('Please enter the review code.')
			return
		}

		setReviewBusy(true)

		try {
			const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')

			const res = await fetch(`${base}/iap/google-review-access`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
				},
				body: JSON.stringify({
					userId: effectiveUserId,
					reviewCode: reviewCode.trim(),
				}),
			})

			const data = await res.json()

			if (!res.ok || !data.success) {
				setReviewMsg(data.message || 'Invalid review code.')
				return
			}

			setReviewMsg('Access approved. Opening app…')

			setTimeout(() => {
				window.location.reload()
			}, 500)
		} catch (err) {
			console.error('[GOOGLE REVIEW] Access error:', err)

			setReviewMsg('Unable to verify reviewer access. Please try again.')
		} finally {
			setReviewBusy(false)
		}
	}

	return (
		<div style={styles.wrap}>
			<div style={styles.card}>
				<h1 style={styles.title}>Shirey Stats &amp; Standings</h1>

				<p style={styles.subtitle}>
					Unlock full access to live scores, schedules, standings, and the
					archive.
				</p>

				<ul style={styles.featureList}>
					<li style={styles.featureItem}>
						Real-time updates during tournaments
					</li>
					<li style={styles.featureItem}>
						Team pages, player stats, historical results
					</li>
					<li style={styles.featureItem}>
						Support ongoing events &amp; new features
					</li>
				</ul>

				<div style={styles.plansWrap}>
					{/* ✅ Yearly — iOS ONLY */}
					{inAppApple && isIOS && (
						<div style={{ ...styles.plan, ...styles.featuredPlan }}>
							<div style={styles.badge}>Best Value</div>

							<div style={styles.planHeader}>Yearly</div>
							<div style={styles.priceRow}>
								<span style={styles.price}>$16.99</span>
								<span style={styles.priceSubInline}>per year</span>
							</div>
							<div style={styles.savings}>Save 29%</div>

							{inAppApple ? (
								<SubscribeButton
									userId={userId}
									productId='com.shirey.stats.yearly'
									buttonText='Choose Yearly'
									subText='Full access for 1 year'
									featured
								/>
							) : (
								<div style={styles.comingSoonBox}>
									Yearly Apple subscription available in the iOS app
								</div>
							)}
						</div>
					)}

					<div style={styles.plan}>
						<div style={styles.planHeader}>Monthly</div>
						<div style={styles.priceRow}>
							<span style={styles.price}>$1.99</span>
							<span style={styles.priceSubInline}>per month</span>
						</div>

						<SubscribeButton
							userId={userId}
							productId='com.shirey.stats.monthly'
							buttonText='Choose Monthly'
							subText='Flexible monthly access'
						/>
					</div>
				</div>

				<div style={styles.restoreSection}>
					<button
						onClick={handleRestore}
						disabled={restoreBusy || !inAppApple}
						style={{
							...styles.restoreBtn,
							opacity: restoreBusy || !inAppApple ? 0.6 : 1,
						}}
					>
						{restoreBusy ? 'Restoring…' : 'Restore Access'}
					</button>

					{restoreMsg ? (
						<div style={styles.restoreMsg}>{restoreMsg}</div>
					) : null}
				</div>

				{/* *************************************************************
    
    GOOGLE PLAY REVIEWER ACCESS

    Android only.
    This gives Google Play reviewers a way to bypass the paywall
    without affecting normal customers or disabling subscriptions.

************************************************************* */}

				{isAndroid && (
					<div style={styles.reviewerSection}>
						{!showReviewerAccess ? (
							<button
								type='button'
								onClick={() => {
									setShowReviewerAccess(true)
									setReviewMsg('')
								}}
								style={styles.reviewerLink}
							>
								Google Play Reviewer?
							</button>
						) : (
							<div style={styles.reviewerBox}>
								<div style={styles.reviewerTitle}>
									Google Play Reviewer Access
								</div>

								<input
									type='password'
									value={reviewCode}
									onChange={(e) => setReviewCode(e.target.value)}
									placeholder='Enter review code'
									disabled={reviewBusy}
									style={styles.reviewerInput}
								/>

								<button
									type='button'
									onClick={handleGoogleReviewerAccess}
									disabled={reviewBusy}
									style={{
										...styles.reviewerButton,
										opacity: reviewBusy ? 0.65 : 1,
									}}
								>
									{reviewBusy ? 'Checking…' : 'Continue'}
								</button>

								{reviewMsg ? (
									<div style={styles.reviewerMsg}>{reviewMsg}</div>
								) : null}
							</div>
						)}
					</div>
				)}

				<p style={styles.footnote}>
					Subscriptions auto-renew until canceled. Manage anytime in your Apple
					account settings.
				</p>
			</div>
		</div>
	)
}

const styles = {
	wrap: {
		position: 'fixed',
		inset: 0,
		display: 'grid',
		placeItems: 'center',
		background: 'rgba(0,0,0,0.6)',
		padding: 12,
		zIndex: 9999,
		overflowY: 'auto',
	},
	card: {
		width: '100%',
		maxWidth: 440,
		background: '#fff',
		borderRadius: 20,
		padding: '22px 16px 18px',
		textAlign: 'center',
		boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
		margin: '24px 0',
	},
	title: {
		margin: 0,
		fontSize: 24,
		fontWeight: 800,
		lineHeight: 1.15,
	},
	subtitle: {
		opacity: 0.82,
		margin: '12px 0 16px',
		fontSize: 15,
		lineHeight: 1.45,
	},
	featureList: {
		listStyle: 'disc',
		paddingLeft: 22,
		textAlign: 'left',
		margin: '0 auto 18px',
		maxWidth: 360,
		lineHeight: 1.5,
	},
	featureItem: {
		marginBottom: 6,
		fontSize: 15,
	},
	plansWrap: {
		display: 'flex',
		flexDirection: 'column',
		gap: 14,
		marginTop: 4,
	},
	plan: {
		position: 'relative',
		border: '1px solid #ddd',
		borderRadius: 16,
		padding: '18px 14px 16px',
		background: '#fafafa',
	},
	featuredPlan: {
		border: '2px solid #edcb37',
		background: '#fffdf5',
		boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
		paddingTop: 24,
	},
	badge: {
		position: 'absolute',
		top: -11,
		left: '50%',
		transform: 'translateX(-50%)',
		background: '#edcb37',
		color: '#222',
		fontSize: 12,
		fontWeight: 800,
		padding: '5px 12px',
		borderRadius: 999,
		whiteSpace: 'nowrap',
	},
	planHeader: {
		fontSize: 18,
		fontWeight: 800,
		marginBottom: 8,
	},
	priceRow: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'baseline',
		gap: 8,
		marginBottom: 10,
		flexWrap: 'wrap',
	},
	price: {
		fontSize: 32,
		fontWeight: 800,
		lineHeight: 1,
	},
	priceSubInline: {
		fontSize: 14,
		opacity: 0.75,
	},
	savings: {
		display: 'inline-block',
		fontSize: 14,
		fontWeight: 700,
		color: '#2d6a4f',
		background: '#eef6ea',
		borderRadius: 999,
		padding: '5px 10px',
		marginBottom: 14,
	},
	comingSoonBox: {
		marginTop: 8,
		padding: '12px 10px',
		borderRadius: 10,
		background: '#f3f3f3',
		fontSize: 13,
		opacity: 0.8,
	},
	restoreSection: {
		marginTop: 16,
		paddingTop: 2,
	},
	restoreBtn: {
		padding: '12px 16px',
		borderRadius: 12,
		border: '1px solid #ccc',
		background: '#fff',
		cursor: 'pointer',
		fontWeight: 700,
		fontSize: 16,
		width: '100%',
	},
	restoreMsg: {
		marginTop: 8,
		fontSize: 12,
		opacity: 0.85,
	},
	footnote: {
		fontSize: 12,
		opacity: 0.7,
		marginTop: 14,
		lineHeight: 1.45,
	},
	reviewerSection: {
		marginTop: 12,
		textAlign: 'center',
	},

	reviewerLink: {
		border: 'none',
		background: 'transparent',
		padding: '6px',
		fontSize: 11,
		color: '#777',
		textDecoration: 'underline',
		cursor: 'pointer',
	},

	reviewerBox: {
		marginTop: 6,
		padding: '12px',
		border: '1px solid #ddd',
		borderRadius: 12,
		background: '#f8f8f8',
	},

	reviewerTitle: {
		fontSize: 13,
		fontWeight: 700,
		marginBottom: 10,
	},

	reviewerInput: {
		width: '100%',
		boxSizing: 'border-box',
		padding: '11px 12px',
		border: '1px solid #ccc',
		borderRadius: 10,
		fontSize: 14,
		marginBottom: 10,
	},

	reviewerButton: {
		width: '100%',
		padding: '11px 14px',
		border: 'none',
		borderRadius: 10,
		background: '#555',
		color: '#fff',
		fontSize: 14,
		fontWeight: 700,
		cursor: 'pointer',
	},

	reviewerMsg: {
		marginTop: 8,
		fontSize: 12,
		lineHeight: 1.4,
		opacity: 0.85,
	},
}
